import * as analytics from 'analytics/Analytics';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {ProcessFlowchartNodeType} from '../processFlowchartConfigEditorConstants';

/**
 * Metadata for a node being deleted, used for analytics tracking.
 */
export interface DeletedNodeMetadata {
  nodeName: string;
  nodeType: ProcessFlowchartNodeType;
  executionType: ProcessExecutionType | null;
  taskSchemaId: string | null;
}

/**
 * Builds a combined identifier string from node type and execution type.
 * For task schema nodes: "EntryTaskSchemaNode", "DirectTaskSchemaNode", etc.
 * For non-task nodes: "RouterNode", "StopNode", etc.
 */
function buildNodeTypeIdentifier(
  nodeType: ProcessFlowchartNodeType,
  executionType: ProcessExecutionType | null
): string {
  if (executionType != null) {
    const executionTypeTitle = executionType.charAt(0).toUpperCase() + executionType.slice(1).toLowerCase();
    return `${executionTypeTitle}${nodeType}`;
  }
  return nodeType;
}

/**
 * Determines if a node's task schema is shared with other nodes remaining on the canvas.
 * Returns true if after removing the deleted nodes, at least one other node with the same
 * task schema will remain on the canvas.
 */
function doesNodeShareTaskSchemaWithOtherNodesOnCanvas(
  nodeTaskSchemaId: string | null,
  allCanvasTaskSchemaIds: Array<string>,
  deletedNodeTaskSchemaIds: Array<string | null>
): boolean {
  if (nodeTaskSchemaId == null) {
    return false;
  }

  const remainingTaskSchemaIds = [...allCanvasTaskSchemaIds];
  for (const deletedId of deletedNodeTaskSchemaIds) {
    if (deletedId != null) {
      const index = remainingTaskSchemaIds.indexOf(deletedId);
      if (index !== -1) {
        remainingTaskSchemaIds.splice(index, 1);
      }
    }
  }

  // Check if the node's task schema exists in remaining nodes
  return remainingTaskSchemaIds.includes(nodeTaskSchemaId);
}

/**
 * Fires analytics events for each deleted node.
 */
export function trackNodeRemovalAnalytics(
  deletedNodesMetadata: Array<DeletedNodeMetadata>,
  taskSchemaIdsOnCanvas: Array<string>,
  flowchartType: string
): void {
  const deletedTaskSchemaIds = deletedNodesMetadata.map((node) => node.taskSchemaId);
  const eventGroupId = analytics.makeAnalyticsEventId();

  deletedNodesMetadata.forEach((node) => {
    const nodeTypeIdentifier = buildNodeTypeIdentifier(node.nodeType, node.executionType);
    const isDuplicateTaskSchema = doesNodeShareTaskSchemaWithOtherNodesOnCanvas(
      node.taskSchemaId,
      taskSchemaIdsOnCanvas,
      deletedTaskSchemaIds
    );

    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.NODE_REMOVED_FROM_CANVAS, {
      actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
      eventGroupId,
      nodeType: nodeTypeIdentifier,
      isDuplicateTaskSchema,
      flowchartType,
    });
  });
}
